import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money, xaf } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { reverseGeocode } from "@/lib/geo";
import { GeoMap } from "@/components/GeoMap";
import { AddressInput } from "@/components/AddressInput";
import { LoyaltyBadge } from "@/components/LoyaltyBadge";
import {
  ForkKnife, Storefront, Package, ShoppingCart, Plus, Minus, Trash, Star, MapPin,
  CaretLeft, CheckCircle, Wallet, Money, FileArrowUp, Clock, X,
} from "@phosphor-icons/react";

const EAT_ACTIVE = ["placed", "preparing", "ready", "on_the_way"];
const STATUS_LABEL = {
  placed: "Order placed", preparing: "Restaurant is preparing",
  ready: "Ready — finding a courier", on_the_way: "Courier on the way",
  delivered: "Delivered", cancelled: "Cancelled",
};

function haversineKm(a, b) {
  const R = 6371, r = (d) => (d * Math.PI) / 180;
  const dLat = r(b.lat - a.lat), dLng = r(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(h));
}

function StatusBadge({ status }) {
  const tone = status === "delivered" ? "bg-emerald-500/15 text-emerald-600"
    : status === "cancelled" ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary";
  return <span data-testid="eat-status" className={`rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}>{STATUS_LABEL[status] || status}</span>;
}

function PayToggle({ value, onChange }) {
  return (
    <div className="flex gap-2" data-testid="eat-pay-toggle">
      {[["wallet", "Wallet", Wallet], ["cash", "Cash on delivery", Money]].map(([v, l, Icon]) => (
        <button key={v} data-testid={`pay-${v}`} onClick={() => onChange(v)}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${value === v ? "border-primary bg-primary/10" : "border-border"}`}>
          <Icon size={15} /> {l}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ Order */
function OrderTab({ config }) {
  const token = config.mapbox_token;
  const { user } = useAuth();
  const uid = user?.id;
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [cart, setCart] = useState({});
  const [dropoff, setDropoff] = useState(null);
  const [prox, setProx] = useState(null);
  const [pay, setPay] = useState("wallet");
  const [promo, setPromo] = useState("");
  const [promoDisc, setPromoDisc] = useState(0);
  const [busy, setBusy] = useState(false);
  const xafRate = config.xaf_per_usd;

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(async (p) => {
      const lng = p.coords.longitude, lat = p.coords.latitude;
      setProx([lng, lat]);
      const addr = await reverseGeocode(lng, lat, token);
      setDropoff((cur) => cur || { lng, lat, address: addr || "Current location" });
    }, () => {}, { enableHighAccuracy: true, timeout: 8000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: restaurants } = useQuery({ queryKey: ["eat-restaurants"], queryFn: async () => (await api.get("/eat/restaurants")).data.restaurants });
  const { data: detail } = useQuery({ enabled: !!selected, queryKey: ["eat-restaurant", selected], queryFn: async () => (await api.get(`/eat/restaurants/${selected}`)).data });
  const { data: mine } = useQuery({ queryKey: ["eat-mine"], queryFn: async () => (await api.get("/eat/orders/mine")).data.orders, refetchInterval: 4000 });

  const active = (mine || []).find((o) => o.customer_id === uid && EAT_ACTIVE.includes(o.status));
  const toRate = (mine || []).find((o) => o.customer_id === uid && o.status === "delivered" && !o.rated);
  const shown = active || toRate;

  const { data: liveOrder } = useQuery({
    enabled: !!shown?.id, queryKey: ["eat-order", shown?.id],
    queryFn: async () => (await api.get(`/eat/orders/${shown.id}`)).data.order,
    refetchInterval: shown && EAT_ACTIVE.includes(shown.status) ? 3000 : false, initialData: shown,
  });
  const live = liveOrder || shown;

  const menu = detail?.menu || [];
  const rest = detail?.restaurant;
  const subtotal = menu.reduce((sum, it) => sum + (cart[it.id] || 0) * it.price_cents, 0);
  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const deliveryFee = rest && dropoff
    ? Math.max(config.min_delivery_cents, config.base_delivery_cents + Math.round(config.per_km_cents * haversineKm(rest, dropoff)))
    : 0;

  const place = async () => {
    if (!dropoff) { toast.error("Set a delivery address"); return; }
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([item_id, qty]) => ({ item_id, qty }));
    if (!items.length) { toast.error("Add items to your cart"); return; }
    setBusy(true);
    try {
      await api.post("/eat/orders", { restaurant_id: selected, items, dropoff, payment_method: pay, promo_code: pay === "wallet" ? promo : "" });
      toast.success("Order placed!");
      setCart({}); setSelected(null); setPromo(""); setPromoDisc(0);
      qc.invalidateQueries({ queryKey: ["eat-mine"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    try { await api.post(`/eat/orders/${live.id}/cancel`); toast("Order cancelled"); qc.invalidateQueries({ queryKey: ["eat-mine"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const applyPromo = async () => {
    if (!promo || subtotal <= 0) return;
    try {
      const d = (await api.get(`/promos/validate?code=${encodeURIComponent(promo)}&scope=eat&amount_cents=${subtotal}`)).data;
      setPromoDisc(d.discount_cents); toast.success(`Promo applied — you save ${money(d.discount_cents)}`);
    } catch (e) { setPromoDisc(0); toast.error(apiError(e)); }
  };
  const rate = async (stars) => {
    try { await api.post(`/eat/orders/${live.id}/rate`, { stars }); toast.success("Thanks!"); qc.invalidateQueries({ queryKey: ["eat-mine"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (live) {
    const markers = [
      live.restaurant_loc && { key: "rest", lat: live.restaurant_loc.lat, lng: live.restaurant_loc.lng, color: "#f97316" },
      live.dropoff && { key: "drop", lat: live.dropoff.lat, lng: live.dropoff.lng, color: "#dc2626" },
      live.courier_lat != null && { key: "courier", lat: live.courier_lat, lng: live.courier_lng, color: "#2563eb" },
    ].filter(Boolean);
    return (
      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <GeoMap token={token} markers={markers} />
        <div className="space-y-4 rounded-2xl border border-border bg-card p-5" data-testid="eat-active-panel">
          <div className="flex items-center justify-between">
            <StatusBadge status={live.status} />
            <span className="font-display text-lg font-semibold">{money(live.charged_cents ?? live.total_cents)}</span>
          </div>
          <div>
            <p className="text-sm font-semibold">{live.restaurant_name}</p>
            <p className="text-xs text-muted-foreground">{live.payment_method === "cash" ? "Cash on delivery" : "Paid from wallet"}</p>
          </div>
          <div className="space-y-1 text-sm">
            {live.items?.map((it, i) => (
              <div key={i} className="flex justify-between"><span>{it.qty}× {it.name}</span><span className="text-muted-foreground">{money(it.price_cents * it.qty)}</span></div>
            ))}
            <div className="flex justify-between border-t border-border pt-1 text-xs text-muted-foreground"><span>Delivery</span><span>{money(live.delivery_fee_cents)}</span></div>
            {live.discount_cents > 0 && <div className="flex justify-between text-xs text-emerald-600"><span>Promo {live.promo_code}</span><span>−{money(live.discount_cents)}</span></div>}
          </div>
          {live.courier_name && <p className="flex items-center gap-2 rounded-xl bg-background p-3 text-sm"><Package size={18} className="text-primary" weight="duotone" /> {live.courier_name} is delivering</p>}
          {["placed", "preparing"].includes(live.status) && (
            <button data-testid="eat-cancel-btn" onClick={cancel} className="w-full rounded-full border border-border py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/10">Cancel order</button>
          )}
          {live.status === "delivered" && !live.rated && (
            <div className="rounded-xl bg-background p-4 text-center" data-testid="eat-rating">
              <p className="text-sm font-medium">Rate your order</p>
              <div className="mt-2 flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => <button key={n} data-testid={`eat-star-${n}`} onClick={() => rate(n)} className="text-amber-400 hover:scale-125"><Star size={26} weight="fill" /></button>)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!selected) {
    return (
      <div data-testid="eat-restaurant-list">
        {(restaurants || []).length === 0 && <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No restaurants available yet.</p>}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(restaurants || []).map((r) => (
            <button key={r.id} data-testid={`eat-rest-${r.id}`} onClick={() => { setSelected(r.id); setCart({}); }}
              className="overflow-hidden rounded-2xl border border-border bg-card text-left transition-all hover:-translate-y-0.5 hover:border-primary">
              <div className="h-32 w-full bg-secondary" style={r.image ? { backgroundImage: `url(${r.image})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
                {!r.image && <div className="flex h-full items-center justify-center text-primary"><ForkKnife size={32} weight="duotone" /></div>}
              </div>
              <div className="p-4">
                <p className="font-semibold">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.cuisine || "Food"} {r.rating ? `· ★ ${r.rating}` : ""}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_360px]" data-testid="eat-menu-view">
      <div className="space-y-3">
        <button data-testid="eat-back" onClick={() => setSelected(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><CaretLeft size={16} /> All restaurants</button>
        <h2 className="font-display text-xl font-semibold">{rest?.name}</h2>
        <div className="space-y-2">
          {menu.map((it) => (
            <div key={it.id} data-testid={`menu-item-${it.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
              <div className="min-w-0"><p className="text-sm font-medium">{it.name}</p><p className="truncate text-xs text-muted-foreground">{it.description || it.category}</p><p className="mt-0.5 text-sm font-semibold">{money(it.price_cents)}</p></div>
              <div className="flex items-center gap-2">
                {cart[it.id] > 0 && <>
                  <button data-testid={`menu-minus-${it.id}`} onClick={() => setCart((c) => ({ ...c, [it.id]: Math.max(0, (c[it.id] || 0) - 1) }))} className="flex h-8 w-8 items-center justify-center rounded-full border border-border"><Minus size={14} /></button>
                  <span className="w-4 text-center text-sm font-semibold">{cart[it.id]}</span>
                </>}
                <button data-testid={`menu-plus-${it.id}`} onClick={() => setCart((c) => ({ ...c, [it.id]: (c[it.id] || 0) + 1 }))} className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground"><Plus size={14} weight="bold" /></button>
              </div>
            </div>
          ))}
          {menu.length === 0 && <p className="text-sm text-muted-foreground">This restaurant hasn't added menu items yet.</p>}
        </div>
      </div>

      <div className="space-y-4 rounded-2xl border border-border bg-card p-5" data-testid="eat-cart">
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><ShoppingCart size={18} /> Your cart</h3>
        {cartCount === 0 ? <p className="text-sm text-muted-foreground">Add items from the menu.</p> : (
          <>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{money(subtotal)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Delivery</span><span>{money(deliveryFee)}</span></div>
              {promoDisc > 0 && pay === "wallet" && <div className="flex justify-between text-emerald-600"><span>Promo</span><span>−{money(promoDisc)}</span></div>}
              <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Total</span><span data-testid="eat-total">{money(subtotal + deliveryFee - (pay === "wallet" ? promoDisc : 0))}</span></div>
              <div className="text-right text-xs text-muted-foreground">{xaf(subtotal + deliveryFee - (pay === "wallet" ? promoDisc : 0), xafRate)}</div>
            </div>
            <div className="flex gap-2">
              <input data-testid="eat-promo" value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Promo code"
                className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-sm uppercase outline-none focus:border-primary" />
              <button data-testid="eat-promo-apply" onClick={applyPromo} className="rounded-xl border border-border px-4 text-sm font-semibold">Apply</button>
            </div>
            <AddressInput label="Deliver to" token={token} proximity={prox} value={dropoff} onSelect={setDropoff} testid="eat-dropoff" />
            <PayToggle value={pay} onChange={setPay} />
            <button data-testid="eat-place-btn" onClick={place} disabled={busy}
              className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-50">
              {busy ? "Placing…" : `Place order · ${money(subtotal + deliveryFee - (pay === "wallet" ? promoDisc : 0))}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Sell */
function RestaurantForm({ token, onDone }) {
  const [f, setF] = useState({ name: "", cuisine: "", image: "", owner_name: "", business_reg_number: "", phone: "" });
  const [loc, setLoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name || !f.owner_name || !loc) { toast.error("Add owner name, business name and address"); return; }
    setBusy(true);
    try { await api.post("/eat/vendor/restaurant", { ...f, address: loc.address, lat: loc.lat, lng: loc.lng, active: true }); toast.success("Registered — now upload your business documents for approval"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const inp = (k, ph) => <input data-testid={`vendor-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />;
  return (
    <div className="mx-auto max-w-md space-y-3 rounded-2xl border border-border bg-card p-6" data-testid="vendor-register">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary"><Storefront size={22} weight="duotone" /></div>
        <div><h2 className="font-display text-lg font-semibold">Restaurant Business Owner Registration</h2><p className="text-sm text-muted-foreground">Register your food business. A community inspector approves it before you go live.</p></div>
      </div>
      {inp("owner_name", "Business owner full name")}
      {inp("name", "Restaurant / business name")}
      <div className="grid grid-cols-2 gap-3">{inp("business_reg_number", "Business reg. no.")}{inp("phone", "Contact phone")}</div>
      {inp("cuisine", "Cuisine (e.g. Grill, Local)")}
      {inp("image", "Image URL (optional)")}
      <AddressInput label="Business location" token={token} value={loc} onSelect={setLoc} testid="vendor-address" />
      <button data-testid="vendor-register-btn" onClick={submit} disabled={busy} className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? "Saving…" : "Register business"}</button>
    </div>
  );
}

const REST_DOC_DEFS = [
  { key: "business_license", label: "Business license" },
  { key: "owner_id", label: "Owner photo ID" },
  { key: "storefront_photo", label: "Storefront photo" },
];

function DocUploader({ endpoint, defs, documents = [], onUploaded, testid }) {
  const [busy, setBusy] = useState("");
  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    try {
      const fd = new FormData(); fd.append("doc_type", key); fd.append("file", file);
      await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); onUploaded?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      {defs.map((d) => {
        const done = documents.includes(d.key);
        return (
          <label key={d.key} data-testid={`${testid}-${d.key}`} className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 text-sm hover:border-primary/40">
            <span className="flex items-center gap-2">{done ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <FileArrowUp size={18} className="text-muted-foreground" />}{d.label}</span>
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold">{busy === d.key ? "Uploading…" : done ? "Replace" : "Upload"}</span>
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(d.key, e.target.files?.[0])} />
          </label>
        );
      })}
    </div>
  );
}

function ApprovalGate({ status, notes, children }) {
  if (status === "approved") return children;
  const rejected = status === "rejected";
  return (
    <div className={`rounded-2xl border p-5 ${rejected ? "border-destructive/40 bg-destructive/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="approval-gate">
      <div className="flex items-center gap-2">
        {rejected ? <X size={20} className="text-destructive" /> : <Clock size={20} className="text-amber-600" weight="fill" />}
        <h3 className="font-display text-base font-semibold">{rejected ? "Not approved" : "Under review"}</h3>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{rejected ? (notes || "Your application wasn't approved. Re-upload clear documents and we'll review again.") : "Thanks! Upload the documents below. A community inspector verifies them before you go live."}</p>
    </div>
  );
}

function SellTab() {
  const qc = useQueryClient();
  const [item, setItem] = useState({ name: "", price: "", description: "", category: "" });
  const { data: cfg } = useQuery({ queryKey: ["eat-config"], queryFn: async () => (await api.get("/eat/config")).data });
  const { data, refetch } = useQuery({ queryKey: ["vendor-restaurant"], queryFn: async () => (await api.get("/eat/vendor/restaurant")).data });
  const { data: orders } = useQuery({ enabled: !!data?.restaurant, queryKey: ["vendor-orders"], queryFn: async () => (await api.get("/eat/vendor/orders")).data.orders, refetchInterval: 5000 });

  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  if (!data.restaurant) return <RestaurantForm token={cfg?.mapbox_token} onDone={refetch} />;

  const rest = data.restaurant;
  if (rest.approval_status !== "approved") {
    return (
      <div className="mx-auto max-w-md space-y-4" data-testid="vendor-pending">
        <ApprovalGate status={rest.approval_status} notes={rest.inspection?.notes} />
        <div>
          <p className="mb-2 text-sm font-semibold">Business documents</p>
          <DocUploader endpoint="/eat/vendor/document" defs={REST_DOC_DEFS} documents={rest.documents} onUploaded={refetch} testid="vendor-docs" />
        </div>
        <button data-testid="vendor-refresh" onClick={refetch} className="w-full rounded-full border border-border py-2.5 text-sm font-semibold transition-colors hover:border-primary">Refresh status</button>
      </div>
    );
  }
  const addItem = async () => {
    if (!item.name || !item.price) { toast.error("Name and price required"); return; }
    try {
      await api.post("/eat/vendor/menu", { restaurant_id: rest.id, name: item.name, price_cents: Math.round(Number(item.price) * 100), description: item.description, category: item.category, available: true });
      toast.success("Item added"); setItem({ name: "", price: "", description: "", category: "" }); refetch();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delItem = async (id) => { try { await api.delete(`/eat/vendor/menu/${id}`); refetch(); } catch (e) { toast.error(apiError(e)); } };
  const vendorAdvance = async (id, status) => { try { await api.post(`/eat/orders/${id}/vendor-status`, { status }); qc.invalidateQueries({ queryKey: ["vendor-orders"] }); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="vendor-dashboard">
      <div className="space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2"><Storefront size={20} className="text-primary" weight="duotone" /><h2 className="font-display text-lg font-semibold">{rest.name}</h2></div>
          <p className="text-xs text-muted-foreground">{rest.cuisine} · {rest.address}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-display text-base font-semibold">Add menu item</h3>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <input data-testid="menu-name" value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} placeholder="Item name" className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            <input data-testid="menu-price" type="number" step="0.25" value={item.price} onChange={(e) => setItem({ ...item, price: e.target.value })} placeholder="Price $" className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            <input data-testid="menu-cat" value={item.category} onChange={(e) => setItem({ ...item, category: e.target.value })} placeholder="Category" className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            <input data-testid="menu-desc" value={item.description} onChange={(e) => setItem({ ...item, description: e.target.value })} placeholder="Description" className="rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <button data-testid="menu-add-btn" onClick={addItem} className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Add item</button>
          <div className="mt-4 space-y-2">
            {(data.menu || []).map((it) => (
              <div key={it.id} className="flex items-center justify-between rounded-xl bg-background px-3 py-2 text-sm">
                <span>{it.name} · {money(it.price_cents)}</span>
                <button data-testid={`menu-del-${it.id}`} onClick={() => delItem(it.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3" data-testid="vendor-orders">
        <h3 className="font-display text-base font-semibold">Incoming orders</h3>
        {(orders || []).filter((o) => o.status !== "cancelled").length === 0 && <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No orders yet.</p>}
        {(orders || []).map((o) => (
          <div key={o.id} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between"><StatusBadge status={o.status} /><span className="font-semibold text-emerald-600">+{money(o.restaurant_earnings_cents)}</span></div>
            <div className="mt-2 text-xs text-muted-foreground">{o.items?.map((it) => `${it.qty}× ${it.name}`).join(", ")}</div>
            <div className="mt-3 flex gap-2">
              {o.status === "placed" && <button data-testid={`vendor-prep-${o.id}`} onClick={() => vendorAdvance(o.id, "preparing")} className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground">Start preparing</button>}
              {o.status === "preparing" && <button data-testid={`vendor-ready-${o.id}`} onClick={() => vendorAdvance(o.id, "ready")} className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground">Mark ready</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Deliver */
function DeliverTab({ config }) {
  const token = config.mapbox_token;
  const { user } = useAuth();
  const uid = user?.id;
  const qc = useQueryClient();
  const watchRef = useRef(null);

  const { data: jobs, isError } = useQuery({ enabled: config.is_driver, queryKey: ["eat-jobs"], queryFn: async () => (await api.get("/eat/courier/jobs")).data.orders, refetchInterval: 5000, retry: false });
  const { data: mine } = useQuery({ queryKey: ["eat-mine"], queryFn: async () => (await api.get("/eat/orders/mine")).data.orders, refetchInterval: 4000 });
  const active = (mine || []).find((o) => o.courier_id === uid && o.status === "on_the_way");

  useEffect(() => {
    if (active && navigator.geolocation) {
      watchRef.current = navigator.geolocation.watchPosition((p) => {
        api.post(`/eat/orders/${active.id}/location`, { lat: p.coords.latitude, lng: p.coords.longitude }).catch(() => {});
      }, () => {}, { enableHighAccuracy: true, maximumAge: 5000 });
    }
    return () => { if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } };
  }, [active?.id]);

  const claim = async (id) => { try { await api.post(`/eat/orders/${id}/claim`); toast.success("Delivery accepted"); qc.invalidateQueries({ queryKey: ["eat-mine"] }); } catch (e) { toast.error(apiError(e)); } };
  const delivered = async () => { try { await api.post(`/eat/orders/${active.id}/delivered`); toast.success("Delivered!"); qc.invalidateQueries({ queryKey: ["eat-mine"] }); } catch (e) { toast.error(apiError(e)); } };

  if (!config.is_driver) {
    return (
      <div className="mx-auto max-w-md rounded-2xl border border-dashed border-border p-8 text-center" data-testid="deliver-not-driver">
        <Package size={36} className="mx-auto text-muted-foreground" weight="duotone" />
        <p className="mt-3 text-sm text-muted-foreground">To deliver food, register & get approved as a driver in <a href="/app/rides" className="font-semibold text-primary">iYeneh Ride</a>. The same account delivers rides and food.</p>
      </div>
    );
  }
  if (isError) {
    return <div className="mx-auto max-w-md rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6 text-center text-sm" data-testid="deliver-pending">Your driver account is pending approval. Complete your documents in iYeneh Ride first.</div>;
  }

  if (active) {
    const markers = [
      active.restaurant_loc && { key: "rest", lat: active.restaurant_loc.lat, lng: active.restaurant_loc.lng, color: "#f97316" },
      active.dropoff && { key: "drop", lat: active.dropoff.lat, lng: active.dropoff.lng, color: "#dc2626" },
      active.courier_lat != null && { key: "me", lat: active.courier_lat, lng: active.courier_lng, color: "#2563eb" },
    ].filter(Boolean);
    return (
      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <GeoMap token={token} markers={markers} />
        <div className="space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="deliver-active">
          <div className="flex items-center justify-between"><StatusBadge status={active.status} /><span className="font-semibold text-emerald-600">+{money(active.courier_earnings_cents)}</span></div>
          <p className="text-sm font-semibold">{active.restaurant_name}</p>
          <p className="flex items-start gap-2 text-sm"><MapPin size={15} weight="fill" className="mt-0.5 text-orange-500" /> {active.restaurant_loc?.address}</p>
          <p className="flex items-start gap-2 text-sm"><MapPin size={15} weight="fill" className="mt-0.5 text-destructive" /> {active.dropoff?.address}</p>
          <button data-testid="deliver-done-btn" onClick={delivered} className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground"><CheckCircle size={16} weight="fill" className="mr-1 inline" /> Mark delivered</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="deliver-jobs">
      <p className="text-sm font-semibold">Delivery jobs</p>
      {(jobs || []).length === 0 && <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No deliveries ready right now.</p>}
      {(jobs || []).map((o) => (
        <div key={o.id} data-testid={`eat-job-${o.id}`} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between"><span className="text-sm font-semibold">{o.restaurant_name}</span><span className="font-semibold text-emerald-600">+{money(o.courier_earnings_cents)}</span></div>
          <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground"><MapPin size={13} weight="fill" className="mt-0.5 text-orange-500" /> {o.restaurant_loc?.address}</p>
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground"><MapPin size={13} weight="fill" className="mt-0.5 text-destructive" /> {o.dropoff?.address}</p>
          <button data-testid={`eat-claim-${o.id}`} onClick={() => claim(o.id)} className="mt-3 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground">Accept delivery</button>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ Page */
export default function Eat() {
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("onboard") ? "sell" : "order");
  const { data: config, isLoading } = useQuery({ queryKey: ["eat-config"], queryFn: async () => (await api.get("/eat/config")).data });

  if (isLoading) return <div className="text-muted-foreground">Loading…</div>;
  if (!config?.enabled) return (
    <div className="mx-auto max-w-md rounded-2xl border border-dashed border-border p-10 text-center" data-testid="eat-disabled">
      <ForkKnife size={40} className="mx-auto text-muted-foreground" weight="duotone" />
      <h1 className="mt-3 font-display text-xl font-semibold">Food ordering coming soon</h1>
    </div>
  );
  if (!config?.mapbox_token) return (
    <div className="mx-auto max-w-md rounded-2xl border border-dashed border-border p-10 text-center" data-testid="eat-no-map">
      <ForkKnife size={40} className="mx-auto text-muted-foreground" weight="duotone" />
      <h1 className="mt-3 font-display text-xl font-semibold">Map not configured</h1>
    </div>
  );

  const tabs = [["order", "Order", ForkKnife], ["sell", "Sell", Storefront], ["deliver", "Deliver", Package]];
  return (
    <div className="space-y-6" data-testid="eat-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">iYeneh Eat</h1>
          <p className="mt-1 text-muted-foreground">Order food, sell from your kitchen, or deliver to earn.</p>
          <div className="mt-2"><LoyaltyBadge /></div>
        </div>
        <div className="flex rounded-full border border-border bg-card p-1">
          {tabs.map(([v, l, Icon]) => (
            <button key={v} data-testid={`eat-tab-${v}`} onClick={() => setTab(v)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <Icon size={16} weight="bold" /> {l}
            </button>
          ))}
        </div>
      </div>
      {tab === "order" ? <OrderTab config={config} /> : tab === "sell" ? <SellTab /> : <DeliverTab config={config} />}
    </div>
  );
}
